import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Input from '../shared/TailwindComponents/Input';
import Button from '../shared/TailwindComponents/Button';
import type { ContractObligation, ObligationTaskOverrides } from '../../utils/contractsApi';

/**
 * Create-task-from-obligation editor (suggest→promote), aligned with the matter
 * "Suggested task" flow: the obligation is a suggestion; you review/edit the task
 * (title, description, priority, due) before it's created. Collaborators and the
 * rest hang off the saved task afterwards (open the task to add them).
 *
 * Rendered as `showModal({ type: 'custom', content: <PromoteObligationForm .../> })`.
 */
const INPUT = 'mt-1 w-full rounded-md border border-hairline bg-surface-sunken/50 px-3 py-2 text-sm text-fg-strong focus:border-champagne/60 focus:outline-none';

export const PromoteObligationForm: React.FC<{
  obligation: ContractObligation;
  contractTitle: string;
  onCreate: (overrides: ObligationTaskOverrides) => void | Promise<void>;
  onCancel: () => void;
}> = ({ obligation, contractTitle, onCreate, onCancel }) => {
  const { t } = useTranslation();
  const [title, setTitle] = useState(obligation.description || '');
  const [description, setDescription] = useState(
    t("Fulfils a contract obligation in '{{c}}'.", { c: contractTitle }),
  );
  const [priority, setPriority] = useState<'low' | 'medium' | 'high'>('medium');
  const [due, setDue] = useState(obligation.due_date ? obligation.due_date.slice(0, 10) : '');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await onCreate({ title: title.trim(), description, priority, due_date: due || null });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="px-4 space-y-4">
      <label className="block text-[11px] font-medium text-fg-muted">{t('Task title')}
        <Input type="text" value={title} autoFocus onChange={(e: any) => setTitle(e.target.value)}
          placeholder={t('What needs to be done')} />
      </label>

      <label className="block text-[11px] font-medium text-fg-muted">{t('Description')}
        <textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} className={INPUT} />
      </label>

      <div className="grid grid-cols-2 gap-3">
        <label className="block text-[11px] font-medium text-fg-muted">{t('Priority')}
          <select value={priority} onChange={(e) => setPriority(e.target.value as any)} className={INPUT}>
            <option value="low">{t('Low')}</option>
            <option value="medium">{t('Medium')}</option>
            <option value="high">{t('High')}</option>
          </select>
        </label>
        <label className="block text-[11px] font-medium text-fg-muted">{t('Due date')}
          <input type="date" value={due} onChange={(e) => setDue(e.target.value)} className={INPUT} />
        </label>
      </div>

      <p className="text-[11px] text-fg-muted">{t('Assigned to you. Open the task afterwards to add collaborators.')}</p>

      <div className="flex justify-end gap-2 pt-1">
        <Button variant="outline" size="small" onClick={onCancel} disabled={busy}>{t('Cancel')}</Button>
        <Button variant="primary" size="small" isLoading={busy} disabled={!title.trim()} onClick={submit}>
          {t('Create task')}
        </Button>
      </div>
    </div>
  );
};

export default PromoteObligationForm;
